import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PickLine, SubstituteOption } from '@popcart/core/v1';
import { api, errorStatus } from '../../api';
import { Icon } from '../../ui/Icon';
import { ProductArt } from '../../catalog/art';
import { peso } from '../../lib/format.js';
import { allowedSubstitutes, chargedText, clampQty, fullName, optionFromProduct, pickInput, previewPick, subMode, subRule } from './lib/pick.js';
import { martKeys } from './keys';

/** Options for a line, restricted by the customer's mode. Mode 'product' falls back to the shop's own product record. */
async function loadOptions(shopId: string, line: PickLine, missing: number): Promise<SubstituteOption[]> {
  const mode = subMode(line);
  if (mode === 'none') return [];
  const list = await api.substitutes(shopId, line.productId, missing);
  const allowed = allowedSubstitutes(line, list);
  if (mode !== 'product' || allowed.length || !line.substitute?.productId) return allowed;
  try {
    const p = await api.adminProduct(shopId, line.substitute.productId);
    if (p.status !== 'active') return [];
    const ext = p as typeof p & { variantLabel?: string | null };
    return allowedSubstitutes(line, [optionFromProduct({ ...p, variantLabel: ext.variantLabel ?? null }, line.unitCentavos)]);
  } catch (err) {
    if (errorStatus(err) === 404) return [];
    throw err;
  }
}

/**
 * "Short": how many were found, then a substitute (what the customer pays, how many) or
 * "Leave it out". The choice is limited by what the customer asked for.
 */
export function SubstituteSheet({
  shopId,
  line,
  initialFound,
  busy,
  error,
  onSave,
  onClose,
}: {
  shopId: string;
  line: PickLine;
  initialFound: number;
  busy: boolean;
  error: string | null;
  onSave(pickedQty: number, sub: { productId: string; qty: number } | null): void;
  onClose(): void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [found, setFound] = useState(() => clampQty(initialFound, line.qty - 1));
  const missing = line.qty - found;
  const mode = subMode(line);
  const opts = useQuery({
    queryKey: martKeys.substitutes(shopId, line.orderItemId, line.qty),
    queryFn: () => loadOptions(shopId, line, line.qty),
    enabled: mode !== 'none',
    staleTime: 15_000,
  });
  const options = opts.data ?? [];
  const [choice, setChoice] = useState<string>(''); // '' = leave it out
  const [subQty, setSubQty] = useState(missing);
  const chosen = options.find((o) => o.productId === choice) ?? null;
  const maxSub = chosen ? Math.min(missing, chosen.available) : 0;
  const qty = clampQty(subQty, maxSub);

  // Default to the first allowed option once they load (the closest match), with as many as are missing.
  useEffect(() => {
    if (!choice && options[0]) {
      setChoice(options[0].productId);
      setSubQty(Math.min(missing, options[0].available));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.data]);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  const preview = previewPick(line, found, chosen ? { priceCentavos: chosen.priceCentavos, qty } : null);
  const name = fullName(line);
  const sub = chosen && qty > 0 ? { productId: chosen.productId, qty } : null;

  return (
    <dialog ref={ref} className="dialog mp-sheet" aria-labelledby={`${id}-t`} aria-describedby={`${id}-r`} onClose={onClose}>
      <form
        className="stack s12"
        onSubmit={(e) => {
          e.preventDefault();
          const input = pickInput(found, sub);
          onSave(input.pickedQty, input.substitute ?? null);
        }}
      >
        <div className="stack s4">
          <h2 id={`${id}-t`} className="t-headline">
            Short: {name}
          </h2>
          <p id={`${id}-r`} className="t-caption muted">
            {subRule(line)}
          </p>
        </div>

        <div className="row between wrap">
          <span className="t-label">Found on the shelf</span>
          <div className="stepper" role="group" aria-label={`Found of ${name}`}>
            <button type="button" className="btn icon-btn" aria-label="One fewer found" disabled={found <= 0} onClick={() => setFound((n) => n - 1)}>
              <Icon name="minus" />
            </button>
            <output aria-live="polite">
              {found}/{line.qty}
            </output>
            <button
              type="button"
              className="btn icon-btn"
              aria-label="One more found"
              disabled={found >= line.qty - 1}
              onClick={() => {
                setFound((n) => n + 1);
                setSubQty((q) => Math.min(q, missing - 1));
              }}
            >
              <Icon name="plus" />
            </button>
          </div>
        </div>
        <span className="t-caption muted">
          {missing} missing at {peso(line.unitCentavos)} each.
        </span>

        <fieldset className="mp-opts">
          <legend className="t-label">Instead</legend>
          {mode !== 'none' ? (
            opts.isLoading ? (
              <span className="t-caption muted">Finding replacements…</span>
            ) : opts.isError ? (
              <span className="t-caption muted">Couldn’t load replacements. You can still leave it out.</span>
            ) : !options.length ? (
              <span className="t-caption muted">{mode === 'product' ? 'Their chosen item isn’t in stock.' : 'Nothing similar is in stock.'}</span>
            ) : null
          ) : null}
          {options.map((o) => {
            const c = chargedText(o, line.unitCentavos);
            return (
              <label key={o.productId} className="mp-opt" data-checked={choice === o.productId || undefined}>
                <input
                  type="radio"
                  name={`${id}-sub`}
                  value={o.productId}
                  checked={choice === o.productId}
                  onChange={() => {
                    setChoice(o.productId);
                    setSubQty(Math.min(missing, o.available));
                  }}
                />
                <ProductArt className="mp-opt-art" name={o.name} line="mart" />
                <span className="stack s4 grow">
                  <b className="t-label">{fullName(o)}</b>
                  <span className="t-caption">{c.text}</span>
                  <span className="t-monocap muted">{[o.shelf ? `Shelf ${o.shelf}` : null, `${o.available} in stock`].filter(Boolean).join(' · ')}</span>
                </span>
              </label>
            );
          })}
          <label className="mp-opt" data-checked={choice === '' || undefined}>
            <input type="radio" name={`${id}-sub`} value="" checked={choice === ''} onChange={() => setChoice('')} />
            <span className="stack s4 grow">
              <b className="t-label">Leave it out</b>
              <span className="t-caption">The customer isn’t charged for the {missing} missing.</span>
            </span>
          </label>
        </fieldset>

        {chosen ? (
          <div className="row between wrap">
            <span className="t-label">How many {fullName(chosen)}</span>
            <div className="stepper" role="group" aria-label={`How many ${fullName(chosen)}`}>
              <button type="button" className="btn icon-btn" aria-label="One fewer replacement" disabled={qty <= 1} onClick={() => setSubQty(qty - 1)}>
                <Icon name="minus" />
              </button>
              <output aria-live="polite">
                {qty}/{maxSub}
              </output>
              <button type="button" className="btn icon-btn" aria-label="One more replacement" disabled={qty >= maxSub} onClick={() => setSubQty(qty + 1)}>
                <Icon name="plus" />
              </button>
            </div>
          </div>
        ) : null}

        <div className="note">
          <b className="t-label">{preview.text}</b>
        </div>

        {error ? (
          <div className="status-msg st-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-quiet" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <button type="submit" className="btn" disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Saving…' : sub ? `Save · swap ${qty}` : 'Save · leave out'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
